import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Building2, AlertCircle, CheckCircle, Clock, CreditCard, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiClient } from '../../utils/apiHelper';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeePayment = () => {
  const [payments, setPayments] = useState([]);
  const [hostelStudents, setHostelStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [banks, setBanks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('payments');
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  const [dueReport, setDueReport] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [dueMonths, setDueMonths] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]); // Multiple month selection
  const [feeType, setFeeType] = useState('monthly'); // 'monthly' or 'onetime'
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    payment_number: `PAY-${Date.now()}`,
    receipt_number: `RCP-${Date.now()}`,
    payer_type: 'Student',
    student_id: '',
    payer_name: '',
    hostel_id: '',
    hostel_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    month: '',
    year: new Date().getFullYear(),
    fee_type: 'Monthly Fee',
    amount: 0,
    payment_mode: 'Cash',
    bank_id: '',
    bank_name: '',
    received_by: 'Admin'
  });

  useEffect(() => {
    fetchPayments();
    fetchHostelStudents();
    fetchHostels();
    fetchBanks();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  useEffect(() => {
    if (hostelStudents.length > 0 && payments.length >= 0) {
      calculateDueReport();
    }
  }, [hostelStudents, payments, hostels]);

  const fetchPayments = async () => {
    try {
      const response = await apiClient.get('/api/hostel/fee-payments');
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHostelStudents = async () => {
    try {
      const response = await apiClient.get('/api/hostel/students');
      const students = Array.isArray(response.data) ? response.data : [];
      setHostelStudents(students.filter(s => s.status === 'Active'));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await apiClient.get('/api/master-settings/banks');
      setBanks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Get hostel fee settings
  const getHostelFees = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    return {
      monthly_fee: hostel?.monthly_fee || 0,
      security_deposit: hostel?.security_deposit || 0,
      admission_fee: hostel?.admission_fee || 0,
      maintenance_fee: hostel?.maintenance_fee || 0,
      total_monthly: (hostel?.monthly_fee || 0) + (hostel?.maintenance_fee || 0),
      total_onetime: (hostel?.security_deposit || 0) + (hostel?.admission_fee || 0)
    };
  };

  // Check if one-time fees are paid
  const checkOneTimeFeesPaid = (student) => {
    const oneTimePayments = payments.filter(p => 
      (p.student_id === student.id || p.payer_name === student.student_name) &&
      (p.fee_type === 'Security Deposit' || p.fee_type === 'Admission Fee')
    );
    
    const securityPaid = oneTimePayments.some(p => p.fee_type === 'Security Deposit');
    const admissionPaid = oneTimePayments.some(p => p.fee_type === 'Admission Fee');
    
    return { securityPaid, admissionPaid };
  };

  // Calculate due months for monthly fee
  const calculateDueMonthsForStudent = (student) => {
    const admissionDate = new Date(student.admission_date);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    const paidMonths = payments
      .filter(p => (p.student_id === student.id || p.payer_name === student.student_name) && p.fee_type === 'Monthly Fee')
      .map(p => `${p.month}-${p.year}`);
    
    const dues = [];
    let startYear = admissionDate.getFullYear();
    let startMonth = admissionDate.getMonth();
    
    for (let year = startYear; year <= currentYear; year++) {
      const monthStart = (year === startYear) ? startMonth : 0;
      const monthEnd = (year === currentYear) ? currentMonth : 11;
      
      for (let month = monthStart; month <= monthEnd; month++) {
        const monthName = MONTHS[month];
        const key = `${monthName}-${year}`;
        
        if (!paidMonths.includes(key)) {
          dues.push({ month: monthName, year, key });
        }
      }
    }
    
    return dues;
  };

  // Calculate due report for all students
  const calculateDueReport = () => {
    const report = hostelStudents.map(student => {
      const hostelFees = getHostelFees(student.hostel_id);
      const dues = calculateDueMonthsForStudent(student);
      const { securityPaid, admissionPaid } = checkOneTimeFeesPaid(student);
      
      const monthlyDue = dues.length * hostelFees.total_monthly;
      const securityDue = securityPaid ? 0 : hostelFees.security_deposit;
      const admissionDue = admissionPaid ? 0 : hostelFees.admission_fee;
      const totalDue = monthlyDue + securityDue + admissionDue;
      
      return {
        ...student,
        hostelFees,
        dueMonths: dues,
        dueCount: dues.length,
        monthlyDue,
        securityDue,
        admissionDue,
        securityPaid,
        admissionPaid,
        totalDue,
        status: totalDue === 0 ? 'paid' : dues.length <= 1 && securityPaid && admissionPaid ? 'current' : 'overdue'
      };
    });
    
    report.sort((a, b) => b.totalDue - a.totalDue);
    setDueReport(report);
  };

  const handleStudentChange = (studentId) => {
    const student = hostelStudents.find(s => s.id === studentId);
    if (student) {
      const hostel = hostels.find(h => h.id === student.hostel_id);
      setSelectedStudent(student);
      setSelectedHostel(hostel);
      
      const dues = calculateDueMonthsForStudent(student);
      setDueMonths(dues);
      setSelectedMonths([]); // Reset selected months
      
      const hostelFees = getHostelFees(student.hostel_id);
      
      setFormData({
        ...formData,
        student_id: student.id,
        payer_name: student.student_name,
        hostel_id: student.hostel_id,
        hostel_name: student.hostel_name,
        amount: 0, // Will be calculated based on selected months
        month: '',
        year: new Date().getFullYear()
      });
      
      setFeeType('monthly');
    }
  };

  // Handle multiple month selection
  const handleMonthToggle = (monthKey) => {
    const hostelFees = getHostelFees(selectedStudent?.hostel_id);
    
    setSelectedMonths(prev => {
      let newSelected;
      if (prev.includes(monthKey)) {
        newSelected = prev.filter(m => m !== monthKey);
      } else {
        newSelected = [...prev, monthKey];
      }
      
      // Update amount based on selected months
      const totalAmount = newSelected.length * hostelFees.total_monthly;
      setFormData(f => ({
        ...f,
        amount: totalAmount,
        month: newSelected.length === 1 ? newSelected[0].split('-')[0] : `${newSelected.length} months`,
        year: newSelected.length === 1 ? parseInt(newSelected[0].split('-')[1]) : new Date().getFullYear()
      }));
      
      return newSelected;
    });
  };

  // Select all due months
  const handleSelectAllMonths = () => {
    const hostelFees = getHostelFees(selectedStudent?.hostel_id);
    const allKeys = dueMonths.map(d => d.key);
    setSelectedMonths(allKeys);
    
    const totalAmount = allKeys.length * hostelFees.total_monthly;
    setFormData(f => ({
      ...f,
      amount: totalAmount,
      month: `${allKeys.length} months`,
      year: new Date().getFullYear()
    }));
  };

  // Clear all selected months
  const handleClearMonths = () => {
    setSelectedMonths([]);
    setFormData(f => ({ ...f, amount: 0, month: '' }));
  };

  const handleFeeTypeChange = (type) => {
    setFeeType(type);
    setSelectedMonths([]); // Reset selected months when changing fee type
    if (!selectedStudent) return;
    
    const hostelFees = getHostelFees(selectedStudent.hostel_id);
    
    if (type === 'monthly') {
      setFormData({
        ...formData,
        fee_type: 'Monthly Fee',
        amount: hostelFees.total_monthly,
        month: ''
      });
    } else if (type === 'security') {
      setFormData({
        ...formData,
        fee_type: 'Security Deposit',
        amount: hostelFees.security_deposit,
        month: 'N/A'
      });
    } else if (type === 'admission') {
      setFormData({
        ...formData,
        fee_type: 'Admission Fee',
        amount: hostelFees.admission_fee,
        month: 'N/A'
      });
    }
  };

  const handleMonthSelect = (monthKey) => {
    const [month, year] = monthKey.split('-');
    setFormData({
      ...formData,
      month,
      year: parseInt(year)
    });
  };

  const handleBankChange = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    if (bank) {
      setFormData({ ...formData, bank_id: bankId, bank_name: bank.bank_name });
    }
  };

  const isBankPayment = formData.payment_mode !== 'Cash';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (feeType === 'monthly' && selectedMonths.length === 0) {
      alert('Please select at least one month');
      return;
    }
    
    if (isBankPayment && !formData.bank_id) {
      alert('Please select a bank');
      return;
    }
    
    setLoading(true);
    try {
      const hostelFees = getHostelFees(selectedStudent?.hostel_id);
      
      // For multiple months, create separate payment entries
      if (feeType === 'monthly' && selectedMonths.length > 0) {
        const paymentPromises = selectedMonths.map(async (monthKey, index) => {
          const [month, year] = monthKey.split('-');
          const payload = {
            ...formData,
            id: `PAY-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
            receipt_number: `RCP-${Date.now()}-${index}`,
            month: month,
            year: parseInt(year),
            amount: hostelFees.total_monthly,
            fee_type: 'Monthly Fee'
          };
          return apiClient.post('/api/hostel/fee-payments', payload);
        });
        
        await Promise.all(paymentPromises);
        
        fetchPayments();
        handleCloseModal();
        
        // Show combined receipt for multiple months
        const monthsList = selectedMonths.map(m => {
          const [month, year] = m.split('-');
          return `${month} ${year}`;
        }).join(', ');
        
        setReceiptData({
          ...formData,
          receipt_number: `RCP-MULTI-${Date.now()}`,
          fee_type: 'Monthly Fee',
          month: monthsList,
          months_paid: selectedMonths.length,
          amount: formData.amount,
          hostel_name: selectedStudent?.hostel_name
        });
        setIsReceiptModalOpen(true);
      } else {
        // Single payment (one-time fees)
        const payload = {
          ...formData,
          id: `PAY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
        
        const response = await apiClient.post('/api/hostel/fee-payments', payload);
        if (response.status === 200 || response.data) {
          fetchPayments();
          handleCloseModal();
          setReceiptData({
            ...payload,
            ...response.data
          });
          setIsReceiptModalOpen(true);
        }
      }
    } catch (error) {
      alert('Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
    setSelectedHostel(null);
    setDueMonths([]);
    setSelectedMonths([]);
    setFeeType('monthly');
    setFormData({
      payment_number: `PAY-${Date.now()}`,
      receipt_number: `RCP-${Date.now()}`,
      payer_type: 'Student',
      student_id: '',
      payer_name: '',
      hostel_id: '',
      hostel_name: '',
      payment_date: new Date().toISOString().split('T')[0],
      month: '',
      year: new Date().getFullYear(),
      fee_type: 'Monthly Fee',
      amount: 0,
      payment_mode: 'Cash',
      bank_id: '',
      bank_name: '',
      received_by: 'Admin'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Paid</span>;
      case 'current':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"><Clock className="w-3 h-3" />Current</span>;
      case 'overdue':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Overdue</span>;
      default:
        return null;
    }
  };

  // Get one-time fees status for selected student
  const getOneTimeFeeStatus = () => {
    if (!selectedStudent) return null;
    const { securityPaid, admissionPaid } = checkOneTimeFeesPaid(selectedStudent);
    const hostelFees = getHostelFees(selectedStudent.hostel_id);
    return { securityPaid, admissionPaid, hostelFees };
  };

  const oneTimeFeeStatus = getOneTimeFeeStatus();

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hostel Fee Collection</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />Collect Fee
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="payments">Payment History</TabsTrigger>
              <TabsTrigger value="due">Due Report</TabsTrigger>
            </TabsList>
            
            {/* Payment History Tab */}
            <TabsContent value="payments">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Receipt #</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Student</th>
                    <th className="text-left p-2">Fee Type</th>
                    <th className="text-left p-2">Month</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-left p-2">Mode</th>
                    <th className="text-center p-2">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-4 text-gray-500">No payments recorded.</td></tr>
                  ) : (
                    payments.map((p) => (
                      <tr key={p.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{p.receipt_number || p.payment_number}</td>
                        <td className="p-2">{formatDate(p.payment_date)}</td>
                        <td className="p-2">{p.payer_name}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 text-xs rounded ${
                            p.fee_type === 'Security Deposit' ? 'bg-purple-100 text-purple-800' :
                            p.fee_type === 'Admission Fee' ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>{p.fee_type}</span>
                        </td>
                        <td className="p-2">{p.month !== 'N/A' ? `${p.month} ${p.year}` : '-'}</td>
                        <td className="p-2 text-right font-medium">₹{p.amount}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 text-xs rounded ${p.payment_mode === 'Cash' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {p.payment_mode}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="outline" onClick={() => { setReceiptData(p); setIsReceiptModalOpen(true); }}>
                            <Printer className="w-3 h-3 mr-1" />Print
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>
            
            {/* Due Report Tab */}
            <TabsContent value="due">
              <div className="mb-4 grid grid-cols-3 gap-4">
                <Card className="p-4 bg-red-50 border-red-200">
                  <p className="text-sm text-red-600">Overdue Students</p>
                  <p className="text-2xl font-bold text-red-700">{dueReport.filter(r => r.status === 'overdue').length}</p>
                </Card>
                <Card className="p-4 bg-yellow-50 border-yellow-200">
                  <p className="text-sm text-yellow-600">Current Due</p>
                  <p className="text-2xl font-bold text-yellow-700">{dueReport.filter(r => r.status === 'current').length}</p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200">
                  <p className="text-sm text-green-600">All Paid</p>
                  <p className="text-2xl font-bold text-green-700">{dueReport.filter(r => r.status === 'paid').length}</p>
                </Card>
              </div>
              
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Student</th>
                    <th className="text-left p-2">Hostel</th>
                    <th className="text-center p-2">Security</th>
                    <th className="text-center p-2">Admission</th>
                    <th className="text-center p-2">Monthly Due</th>
                    <th className="text-right p-2">Total Due</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dueReport.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-4 text-gray-500">No hostel students found.</td></tr>
                  ) : (
                    dueReport.map((student) => (
                      <tr key={student.id} className={`border-b hover:bg-gray-50 ${student.status === 'overdue' ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-medium">{student.student_name}</td>
                        <td className="p-2">{student.hostel_name} - {student.room_number}</td>
                        <td className="p-2 text-center">
                          {student.securityPaid ? (
                            <span className="text-green-600">✓ Paid</span>
                          ) : (
                            <span className="text-red-600">₹{student.securityDue}</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {student.admissionPaid ? (
                            <span className="text-green-600">✓ Paid</span>
                          ) : (
                            <span className="text-red-600">₹{student.admissionDue}</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {student.dueCount > 0 ? (
                            <span className="text-red-600">{student.dueCount} mo (₹{student.monthlyDue})</span>
                          ) : (
                            <span className="text-green-600">Paid</span>
                          )}
                        </td>
                        <td className="p-2 text-right font-bold text-red-600">
                          {student.totalDue > 0 ? `₹${student.totalDue}` : '-'}
                        </td>
                        <td className="p-2 text-center">{getStatusBadge(student.status)}</td>
                        <td className="p-2 text-center">
                          {student.totalDue > 0 && (
                            <Button size="sm" onClick={() => { handleStudentChange(student.id); setIsModalOpen(true); }}>
                              <CreditCard className="w-3 h-3 mr-1" />Pay
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Collect Fee Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Collect Hostel Fee</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Student Selection */}
            <div>
              <Label>Select Student *</Label>
              <Select value={formData.student_id} onValueChange={handleStudentChange}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Student --" />
                </SelectTrigger>
                <SelectContent>
                  {hostelStudents.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.student_name} ({s.hostel_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Student & Hostel Info */}
            {selectedStudent && selectedHostel && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>Student:</strong> {selectedStudent.student_name}</p>
                <p><strong>Hostel:</strong> {selectedHostel.hostel_name} (Room {selectedStudent.room_number})</p>
                <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-blue-200">
                  <p>Monthly Fee: <strong>₹{selectedHostel.monthly_fee || 0}</strong></p>
                  <p>Maintenance: <strong>₹{selectedHostel.maintenance_fee || 0}</strong></p>
                  <p>Security Dep: <strong>₹{selectedHostel.security_deposit || 0}</strong></p>
                  <p>Admission Fee: <strong>₹{selectedHostel.admission_fee || 0}</strong></p>
                </div>
              </div>
            )}

            {/* Fee Type Selection */}
            {selectedStudent && (
              <div>
                <Label>Fee Type *</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Button 
                    type="button" 
                    variant={feeType === 'monthly' ? 'default' : 'outline'}
                    onClick={() => handleFeeTypeChange('monthly')}
                    className="text-xs"
                  >
                    Monthly Fee
                  </Button>
                  <Button 
                    type="button" 
                    variant={feeType === 'security' ? 'default' : 'outline'}
                    onClick={() => handleFeeTypeChange('security')}
                    disabled={oneTimeFeeStatus?.securityPaid}
                    className="text-xs"
                  >
                    {oneTimeFeeStatus?.securityPaid ? '✓ Security' : 'Security Deposit'}
                  </Button>
                  <Button 
                    type="button" 
                    variant={feeType === 'admission' ? 'default' : 'outline'}
                    onClick={() => handleFeeTypeChange('admission')}
                    disabled={oneTimeFeeStatus?.admissionPaid}
                    className="text-xs"
                  >
                    {oneTimeFeeStatus?.admissionPaid ? '✓ Admission' : 'Admission Fee'}
                  </Button>
                </div>
              </div>
            )}

            {/* Month Selection (only for monthly) */}
            {feeType === 'monthly' && dueMonths.length > 0 && (
              <div>
                <Label>Select Month *</Label>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-red-500 font-medium">{dueMonths.length} month(s) due</span>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={handleSelectAllMonths}>
                      Select All
                    </Button>
                    {selectedMonths.length > 0 && (
                      <Button type="button" size="sm" variant="ghost" onClick={handleClearMonths}>
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    {dueMonths.map((d) => (
                      <label 
                        key={d.key} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedMonths.includes(d.key) 
                            ? 'bg-blue-100 border border-blue-300' 
                            : 'bg-white border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedMonths.includes(d.key)}
                          onChange={() => handleMonthToggle(d.key)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">{d.month} {d.year}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {selectedMonths.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    ✓ {selectedMonths.length} month(s) selected
                  </p>
                )}
              </div>
            )}

            {feeType === 'monthly' && dueMonths.length === 0 && selectedStudent && (
              <div className="bg-green-50 p-3 rounded text-green-700 text-sm">
                ✅ All monthly fees paid up to current month!
              </div>
            )}

            {/* Amount Display */}
            {selectedStudent && formData.amount > 0 && (
              <div className="bg-gray-100 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">Amount to Pay:</span>
                    {selectedMonths.length > 1 && (
                      <p className="text-xs text-gray-500">{selectedMonths.length} months × ₹{getHostelFees(selectedStudent?.hostel_id).total_monthly}</p>
                    )}
                  </div>
                  <span className="text-2xl font-bold text-green-600">₹{formData.amount?.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            {/* Payment Mode */}
            {selectedStudent && ((feeType === 'monthly' && selectedMonths.length > 0) || feeType !== 'monthly') && formData.amount > 0 && (
              <>
                <div>
                  <Label>Payment Mode *</Label>
                  <Select value={formData.payment_mode} onValueChange={(v) => setFormData({ ...formData, payment_mode: v, bank_id: '', bank_name: '' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Bank">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isBankPayment && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <Label className="flex items-center gap-2 text-blue-800 mb-2">
                      <Building2 className="w-4 h-4" />Select Bank *
                    </Label>
                    <Select value={formData.bank_id} onValueChange={handleBankChange}>
                      <SelectTrigger><SelectValue placeholder="-- Select Bank --" /></SelectTrigger>
                      <SelectContent>
                        {banks.map((bank) => (
                          <SelectItem key={bank.id} value={bank.id}>
                            {bank.bank_name} (₹{bank.current_balance?.toLocaleString('en-IN')})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={loading || !selectedStudent || formData.amount === 0 || (feeType === 'monthly' && selectedMonths.length === 0)}
              >
                {loading ? 'Processing...' : `Pay ₹${formData.amount?.toLocaleString('en-IN')}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Print Modal */}
      <Dialog open={isReceiptModalOpen} onOpenChange={setIsReceiptModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Payment Receipt
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <div id="receipt-content" className="bg-white p-6 border rounded-lg">
              {/* Receipt Header */}
              <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
                {collegeInfo?.logo_url && (
                  <img src={collegeInfo.logo_url} alt="Logo" className="h-12 mx-auto mb-2" />
                )}
                <h1 className="text-2xl font-bold text-gray-800">HOSTEL FEE RECEIPT</h1>
                <p className="text-gray-600 mt-1">{collegeInfo?.college_name || collegeInfo?.name || 'Institution Name'}</p>
                <p className="text-sm text-gray-500">{collegeInfo?.address || ''}</p>
              </div>

              {/* Receipt Details */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Receipt No:</p>
                  <p className="font-bold text-lg">{receiptData.receipt_number || receiptData.payment_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Date:</p>
                  <p className="font-bold text-lg">{formatDate(receiptData.payment_date)}</p>
                </div>
              </div>

              {/* Student Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Student Name:</p>
                    <p className="font-semibold">{receiptData.payer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Hostel:</p>
                    <p className="font-semibold">{receiptData.hostel_name}</p>
                  </div>
                </div>
              </div>

              {/* Payment Details Table */}
              <table className="w-full border-collapse border border-gray-300 mb-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-2 text-left">Description</th>
                    <th className="border border-gray-300 p-2 text-center">Period</th>
                    <th className="border border-gray-300 p-2 text-right">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-2 font-medium">
                      {receiptData.fee_type}
                      {receiptData.months_paid > 1 && (
                        <span className="text-xs text-gray-500 block">({receiptData.months_paid} months)</span>
                      )}
                    </td>
                    <td className="border border-gray-300 p-2 text-center text-sm">
                      {receiptData.month && receiptData.month !== 'N/A' 
                        ? (receiptData.months_paid > 1 
                            ? receiptData.month 
                            : `${receiptData.month} ${receiptData.year || ''}`)
                        : 'One-time'}
                    </td>
                    <td className="border border-gray-300 p-2 text-right font-bold">{receiptData.amount?.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-green-50">
                    <td colSpan="2" className="border border-gray-300 p-2 text-right font-bold">Total Paid:</td>
                    <td className="border border-gray-300 p-2 text-right font-bold text-green-700 text-lg">₹{receiptData.amount?.toLocaleString('en-IN')}</td>
                  </tr>
                </tfoot>
              </table>

              {/* Payment Mode */}
              <div className="flex justify-between items-center mb-6 p-3 bg-blue-50 rounded">
                <div>
                  <p className="text-sm text-gray-600">Payment Mode:</p>
                  <p className="font-semibold">{receiptData.payment_mode}</p>
                </div>
                {receiptData.bank_name && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Bank:</p>
                    <p className="font-semibold">{receiptData.bank_name}</p>
                  </div>
                )}
              </div>

              {/* Amount in Words */}
              <div className="border-t border-b border-gray-300 py-3 mb-6">
                <p className="text-sm text-gray-600">Amount in Words:</p>
                <p className="font-semibold italic">Rupees {numberToWords(receiptData.amount)} Only</p>
              </div>

              {/* Signature Section */}
              <div className="flex justify-between mt-8 pt-4">
                <div className="text-center">
                  <div className="border-t border-gray-400 w-40 pt-2">
                    <p className="text-sm text-gray-600">Student Signature</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 w-40 pt-2">
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">This is a computer-generated receipt. No signature required for amounts below ₹5,000.</p>
                <p className="text-xs text-gray-400 mt-1">Received by: {receiptData.received_by || 'Admin'}</p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsReceiptModalOpen(false)}>Close</Button>
            <Button onClick={() => {
              const printContent = document.getElementById('receipt-content');
              const printWindow = window.open('', '_blank');
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Hostel Fee Receipt - ${receiptData?.receipt_number || receiptData?.payment_number}</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 20px; }
                      table { width: 100%; border-collapse: collapse; }
                      th, td { border: 1px solid #ccc; padding: 8px; }
                      .text-center { text-align: center; }
                      .text-right { text-align: right; }
                      .font-bold { font-weight: bold; }
                      .text-lg { font-size: 1.1em; }
                      .text-2xl { font-size: 1.5em; }
                      .bg-gray-50 { background: #f9f9f9; }
                      .bg-gray-100 { background: #f3f3f3; }
                      .bg-green-50 { background: #f0fdf4; }
                      .bg-blue-50 { background: #eff6ff; }
                      .text-green-700 { color: #15803d; }
                      .border-b-2 { border-bottom: 2px solid #333; }
                      .grid { display: flex; justify-content: space-between; }
                      .rounded-lg { border-radius: 8px; }
                      .p-4 { padding: 16px; }
                      .p-6 { padding: 24px; }
                      .mb-4 { margin-bottom: 16px; }
                      .mb-6 { margin-bottom: 24px; }
                      .mt-6 { margin-top: 24px; }
                      .mt-8 { margin-top: 32px; }
                      .pt-4 { padding-top: 16px; }
                      .pb-4 { padding-bottom: 16px; }
                      @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                      }
                    </style>
                  </head>
                  <body>
                    ${printContent.innerHTML}
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.print();
            }}>
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper function to convert number to words
const numberToWords = (num) => {
  if (!num || num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const n = Math.floor(num);
  if (n >= 10000000) {
    return convertLessThanThousand(Math.floor(n / 10000000)) + ' Crore ' + numberToWords(n % 10000000);
  }
  if (n >= 100000) {
    return convertLessThanThousand(Math.floor(n / 100000)) + ' Lakh ' + numberToWords(n % 100000);
  }
  if (n >= 1000) {
    return convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand ' + convertLessThanThousand(n % 1000);
  }
  return convertLessThanThousand(n);
};

export default FeePayment;
